import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  setUnauthorizedHandler,
  type Brand,
  type Idea,
  type QueueItem,
  type ReelPreset,
  type ReelTemplate,
  type TemplateMeta,
} from "./api";
import type { Me } from "./api";
import { Header, type HeaderView } from "./components/Header";
import { Login } from "./views/Login";
import { Users } from "./views/Users";
import { Gallery } from "./views/Gallery";
import { Editor } from "./views/Editor";
import { Queue } from "./views/Queue";
import { Composer } from "./views/Composer";
import { Workspace } from "./views/Workspace";
import { Ideas } from "./views/Ideas";
import { Plan } from "./views/Plan";
import { Storyboards } from "./views/Storyboards";
import { Deploy } from "./views/Deploy";
import { Capture } from "./views/Capture";

type View = HeaderView;

type Toast = { kind: "success" | "error" | "info"; message: string } | null;

export const App: React.FC = () => {
  const [view, setView] = useState<View>("gallery");
  const [brand, setBrand] = useState<Brand | null>(null);
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [formats, setFormats] = useState<string[]>([]);
  const [locales, setLocales] = useState<string[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [pickedIdea, setPickedIdea] = useState<Idea | null>(null);
  const [composerPreset, setComposerPreset] = useState<ReelPreset | null>(null);
  const [composerIdea, setComposerIdea] = useState<Idea | null>(null);
  const [composerTemplate, setComposerTemplate] = useState<ReelTemplate | null>(null);
  const [composerWorkspaceId, setComposerWorkspaceId] = useState<string | null>(null);
  const [reelTemplates, setReelTemplates] = useState<ReelTemplate[]>([]);
  const [workspaceCount, setWorkspaceCount] = useState<number>(0);
  const [ideaPoolOpenCount, setIdeaPoolOpenCount] = useState<number>(0);
  const [storyboardOpenCount, setStoryboardOpenCount] = useState<number>(0);
  const [planEntryCount, setPlanEntryCount] = useState<number>(0);
  const [deployCount, setDeployCount] = useState<number>(0);
  const [toast, setToast] = useState<Toast>(null);
  const toastTimer = useRef<number | null>(null);
  // null = noch nicht geprüft; true/false = Login-Status bekannt.
  const [authRequired, setAuthRequired] = useState<boolean>(false);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  const showToast = (kind: NonNullable<Toast>["kind"], message: string): void => {
    setToast({ kind, message });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  };

  const refreshQueue = useCallback(async (): Promise<void> => {
    try {
      const q = await api.queue();
      setQueue(q.items);
    } catch (e) {
      console.warn("queue load failed", e);
    }
  }, []);

  const refreshWorkspace = useCallback(async (): Promise<void> => {
    try {
      const list = await api.workspaceList();
      setWorkspaceCount(list.length);
    } catch (e) {
      console.warn("workspace load failed", e);
    }
  }, []);

  const refreshWorkflowCounters = useCallback(async (): Promise<void> => {
    try {
      const [pool, sbs, plan, deps] = await Promise.all([
        api.ideasPoolList().catch(() => []),
        api.storyboardsList().catch(() => []),
        api.planList().catch(() => []),
        api.deploymentsList().catch(() => []),
      ]);
      setIdeaPoolOpenCount(
        pool.filter((i) => i.status === "new" || i.status === "approved").length,
      );
      setStoryboardOpenCount(
        sbs.filter((s) => s.status === "draft" || s.status === "ready").length,
      );
      setPlanEntryCount(plan.length);
      setDeployCount(deps.length);
    } catch (e) {
      console.warn("workflow counters failed", e);
    }
  }, []);

  const loadAll = useCallback(async (): Promise<void> => {
    try {
      api.authMe().then(setMe).catch(() => setMe(null));
      const [b, t, i, f, l, rt] = await Promise.all([
        api.brand(),
        api.templates(),
        api.ideas(),
        api.formats(),
        api.locales(),
        api.reelTemplates(),
      ]);
      setBrand(b);
      setTemplates(t);
      setIdeas(i);
      setFormats(f);
      setLocales(l);
      setReelTemplates(rt);
      await Promise.all([refreshQueue(), refreshWorkspace(), refreshWorkflowCounters()]);
    } catch (e) {
      showToast("error", `Konnte API nicht laden: ${(e as Error).message}`);
    }
  }, [refreshQueue, refreshWorkspace, refreshWorkflowCounters]);

  // Läuft eine Session ab (401), zurück zum Login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthRequired(true);
      setAuthed(false);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Auth-Status prüfen, danach (falls erlaubt) alles laden.
  useEffect(() => {
    void (async () => {
      let ok = true;
      try {
        const s = await api.authStatus();
        setAuthRequired(s.required);
        ok = s.authenticated;
        setAuthed(ok);
      } catch {
        // Status-Endpoint nicht erreichbar → wie "offen" behandeln und laden.
        setAuthRequired(false);
        setAuthed(true);
      }
      if (ok) await loadAll();
    })();
  }, [loadAll]);

  // Nicht-Admins nie auf dem Benutzer-Tab festhalten.
  useEffect(() => {
    if (view === "users" && me && !me.isAdmin) setView("gallery");
  }, [view, me]);

  const handleLoginSuccess = useCallback((): void => {
    setAuthed(true);
    void loadAll();
  }, [loadAll]);

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await api.authLogout();
    } catch {
      /* noop */
    }
    setMe(null);
    setAuthed(false);
  }, []);

  // Refresh workflow counters whenever we leave a workflow tab so the badges
  // stay roughly in sync without polling.
  useEffect(() => {
    if (view === "ideas" || view === "storyboards" || view === "plan" || view === "deploy") return;
    void refreshWorkflowCounters();
  }, [view, refreshWorkflowCounters]);

  useEffect(() => {
    const hasActive = queue.some((q) => {
      const s = q.runtime?.status ?? q.status;
      return s === "rendering" || s === "pending";
    });
    if (!hasActive) return;
    const id = window.setInterval(() => {
      void refreshQueue();
    }, 1500);
    return () => window.clearInterval(id);
  }, [queue, refreshQueue]);

  const onPickIdea = (idea: Idea): void => {
    setPickedIdea(idea);
    setView("editor");
  };

  const onLoadReel = async (idea: Idea): Promise<void> => {
    try {
      const preset = await api.reelPreset(idea.id);
      setComposerIdea(idea);
      setComposerPreset(preset);
      setComposerTemplate(null);
      setComposerWorkspaceId(null);
      setView("composer");
    } catch (e) {
      showToast("error", `Konnte Reel-Preset nicht laden: ${(e as Error).message}`);
    }
  };

  const onLoadTemplate = async (tplId: string): Promise<void> => {
    try {
      const tpl = await api.reelTemplate(tplId);
      setComposerIdea(null);
      setComposerPreset(null);
      setComposerTemplate(tpl);
      setComposerWorkspaceId(null);
      setView("composer");
    } catch (e) {
      showToast("error", `Konnte Vorlage nicht laden: ${(e as Error).message}`);
    }
  };

  const onEmptyReel = (): void => {
    setComposerIdea(null);
    setComposerPreset(null);
    setComposerTemplate(null);
    setComposerWorkspaceId(null);
    setView("composer");
  };

  const onOpenFromWorkspace = (id: string): void => {
    setComposerIdea(null);
    setComposerPreset(null);
    setComposerTemplate(null);
    setComposerWorkspaceId(id);
    setView("composer");
  };

  const onComposerQueued = (msg: string, opensQueue: boolean): void => {
    void opensQueue;
    showToast("success", msg);
    void refreshQueue();
  };

  const onComposerSaved = (): void => {
    showToast("success", "Reel im Workspace gespeichert.");
    void refreshWorkspace();
  };

  const onSubmit = async (params: {
    template: string;
    category: string;
    hookId?: string;
    ideaId?: string;
    props: Record<string, Record<string, unknown>>;
    languages: string[];
    formats: string[];
    renderNow: boolean;
  }): Promise<void> => {
    try {
      const res = await api.addToQueue(params);
      showToast(
        "success",
        params.renderNow
          ? `${res.id} wird gerendert · ${params.languages.length} Sprachen × ${params.formats.length} Formate`
          : `${res.id} in Queue · ${params.languages.length} Sprachen × ${params.formats.length} Formate`,
      );
      await refreshQueue();
    } catch (e) {
      showToast("error", `Fehler: ${(e as Error).message}`);
    }
  };

  const activeRenderCount = queue.reduce((n, q) => {
    const s = q.runtime?.status ?? q.status;
    return s === "rendering" || s === "pending" ? n + 1 : n;
  }, 0);
  const activeRenderProgress = (() => {
    const inFlight = queue.filter((q) => q.runtime?.status === "rendering");
    if (inFlight.length === 0) return null;
    const sum = inFlight.reduce((acc, q) => acc + (q.runtime?.progress ?? 0), 0);
    return sum / inFlight.length;
  })();

  if (authRequired && authed === false) {
    return <Login onSuccess={handleLoginSuccess} brandName={brand?.name} />;
  }

  return (
    <div className="app">
      <Header
        brand={brand}
        view={view}
        setView={setView}
        queueCount={queue.length}
        workspaceCount={workspaceCount}
        ideaPoolCount={ideaPoolOpenCount}
        storyboardCount={storyboardOpenCount}
        planCount={planEntryCount}
        deployCount={deployCount}
        activeRenderCount={activeRenderCount}
        activeRenderProgress={activeRenderProgress}
        onLogout={authRequired ? () => void handleLogout() : undefined}
        currentUser={me?.username ?? null}
        isAdmin={me?.isAdmin ?? false}
      />
      <main>
        {view === "gallery" && (
          <Gallery
            ideas={ideas}
            templates={reelTemplates}
            onPick={onPickIdea}
            onLoadReel={(i) => void onLoadReel(i)}
            onLoadTemplate={(id) => void onLoadTemplate(id)}
            onEmptyReel={onEmptyReel}
          />
        )}
        {view === "ideas" && (
          <Ideas
            onOpenStoryboards={() => setView("storyboards")}
            showToast={showToast}
          />
        )}
        {view === "plan" && <Plan showToast={showToast} />}
        {view === "storyboards" && (
          <Storyboards
            onOpenComposer={onOpenFromWorkspace}
            showToast={showToast}
          />
        )}
        {view === "workspace" && (
          <Workspace
            onOpen={onOpenFromWorkspace}
            onNew={onEmptyReel}
            showToast={showToast}
            onShowQueue={() => {
              void refreshQueue();
              setView("queue");
            }}
          />
        )}
        {view === "composer" && (
          <Composer
            formats={formats}
            locales={locales}
            initialIdea={composerIdea}
            initialPreset={composerPreset}
            initialTemplate={composerTemplate}
            initialWorkspaceId={composerWorkspaceId}
            onQueued={onComposerQueued}
            onSaved={onComposerSaved}
          />
        )}
        {view === "editor" && (
          <Editor
            templates={templates}
            formats={formats}
            locales={locales}
            initialIdea={pickedIdea}
            onSubmit={onSubmit}
          />
        )}
        {view === "queue" && <Queue items={queue} refresh={refreshQueue} />}
        {view === "deploy" && <Deploy showToast={showToast} />}
        {view === "capture" && <Capture showToast={showToast} />}
        {view === "users" && me?.isAdmin && <Users showToast={showToast} />}
      </main>
      {toast ? <div className={`toast ${toast.kind}`}>{toast.message}</div> : null}
    </div>
  );
};
